import { toVehicle, toVehicleDetail } from "../utils/mappers/to-vehicle";
import { toVehicleLite } from "../utils/mappers/to-vehicle-lite";
import { toSearchVehicleResult } from "../utils/mappers/to-search-vehicle-result";
import { toTelematicsStatus } from "../utils/mappers/to-telematics-status";
import { mapBatteryStatusDtoToBatteryStatus } from "../utils/mappers/map-battery-status-dto-to-battery-status";
import { mapVehicleLockStatusDtoToLockStatus } from "../utils/mappers/map-vehicle-lock-status-dto-to-lock-status";
import {
	getMockDamage3DModelData,
	mockCreateDamage,
	mockDeleteDamage,
	mockToggleDamageStatus,
	mockUpdateDamage,
} from "../mocks/damage-3d-mock";
import type {
	ProviderId,
	TelematicsDeviceInfo,
	UpdateVehicleClientPreferenceRequest,
	Vehicle,
	VehicleClientPreference,
	VehicleLite,
} from "../types/vehicle.types";
import type { Damage } from "@/features/damages/types/damage.types";
import type { OutOfFleetBooking } from "../utils/to-out-of-fleet-impact/to-out-of-fleet-impact";
import type { PutOutOfFleetBooking } from "../utils/split-put-out-of-fleet-bookings/split-put-out-of-fleet-bookings";
import type {
	FleetCreateDamageRequestDto,
	FleetDamageModelDataDto,
	FleetDamageStatusDto,
} from "../types/damage-3d.types";

import type { Period } from "@/types/time.types";
import type {
	BatteryStatus,
	Location,
	LockStatusReturn,
	TelematicsStatus,
} from "@/types/telematics.types";
import {
	BookingStatus,
	type DamageStatusDto,
	type GetVehiclesLiteParams,
	type LocationDto,
	type LockStatusDto,
	type PeriodDto,
	type TelematicsCreateRequestDto,
	type TelematicsUpdateRequestDto,
	TimeZoneDto,
	type UpdateVehicleClientPreferenceRequestDto,
	type UpdateVehicleRequestDto,
	type VehicleUsagePermissionSettingDto,
	changeImmobilizer,
	changeLock,
	createDevice,
	deleteDevice,
	getBatteryStatus,
	getDamages,
	getDeviceByDeviceId,
	getDeviceByVehicle,
	getLocation,
	getLockStatus,
	getMyVehicles,
	getProviders,
	getSupportPageLink,
	getTelematicsStatus,
	getVehicleById,
	getVehicleClientPreference,
	getVehicles,
	getVehiclesForClient,
	getVehiclesLite,
	putVehicleOutOfFleet,
	refreshBattery,
	searchBookingEvents,
	searchBookings,
	searchVehicles,
	updateDamageStatus,
	updateDevice,
	updateLocation,
	updateVehicle,
	updateVehicleClientPreference,
	wakeUp,
} from "@/lib/api";
import { toDamage } from "@/features/damages/utils/mappers/to-damage";
import { AccessMode } from "@/types/accessmode.types";
import { toLocation } from "@/utils/mappers/to-location";
import { cleanWebApiDateString, getUtcISOString } from "@/utils/date.utilities";
import { toUpdatableField } from "@/utils/mappers/to-updatable-field/to-updatable-field";

/** A booking that can still run: awaiting approval, approved or running. */
const OPEN_BOOKING_STATUSES = [
	BookingStatus.AWAITING_APPROVAL,
	BookingStatus.APPROVED,
	BookingStatus.ACTIVE,
];

export const vehiclesService = {
	async getClientVehicles(clientId: string) {
		const response = await getVehiclesForClient(clientId);

		const vehicles = response.vehicles;
		return vehicles.map((dto) => toVehicle(dto));
	},

	async getVehicles() {
		const response = await getVehicles();

		return response.vehicles.map((dto) => toVehicle(dto));
	},

	async getVehiclesLite(
		params?: GetVehiclesLiteParams
	): Promise<Array<VehicleLite>> {
		const response = await getVehiclesLite(params);
		return response.items.map(toVehicleLite);
	},

	async searchVehicles(params: {
		subjectUserId?: string;
		subjectClientId?: string;
		accessMode?: AccessMode;
		location?: LocationDto;
		minimumSeats?: number;
		maxDistance?: number;
		minimumRange?: number;
		period?: Period;
		expressBooking?: boolean;
		vehicleIds?: Array<string>;
		brandIds?: Array<string>;
		modelIds?: Array<string>;
		bodyStyleIds?: Array<string>;
		intentIds?: Array<string>;
		permissions?: Array<VehicleUsagePermissionSettingDto>;
		minDayPrice?: number;
		maxDayPrice?: number;
		onlyFavorites?: boolean;
		instantBooking?: boolean;
	}): Promise<Array<Vehicle>> {
		const {
			subjectUserId,
			subjectClientId,
			accessMode = AccessMode.AGENT,
			location,
			minimumSeats,
			maxDistance,
			minimumRange,
			period,
			expressBooking,
			vehicleIds,
			brandIds,
			modelIds,
			bodyStyleIds,
			intentIds,
			permissions,
			minDayPrice,
			maxDayPrice,
			onlyFavorites,
			instantBooking,
		} = params;

		let periodDto: PeriodDto | undefined;

		if (period) {
			periodDto = {
				start: getUtcISOString(period.start),
				end: getUtcISOString(period.end),
				timeZone: TimeZoneDto.BRUSSELS,
			};
		}

		// Call the API endpoint to search vehicles by name
		const response = await searchVehicles({
			subjectUserId,
			subjectClientId,
			accessMode,
			period: periodDto,
			expressBooking,
			filterCriteria: {
				location,
				minimumSeats,
				maxDistance,
				minimumRange,
				vehicleIds,
				brandIds,
				modelIds,
				bodyStyleIds,
				intentIds,
				permissions,
				minDayPrice,
				maxDayPrice,
				onlyFavorites,
				instantBooking,
			},
		});

		const vehicles = response.vehicles || [];
		return vehicles.map(toSearchVehicleResult);
	},
	async getVehicle(vehicleId: string) {
		return toVehicleDetail(await getVehicleById(vehicleId));
	},
	async getVehicleStatus(vehicleId: string): Promise<TelematicsStatus> {
		const telematicsStatusDto = await getTelematicsStatus(vehicleId);

		return toTelematicsStatus(telematicsStatusDto);
	},
	async getBatteryStatus(vehicleId: string): Promise<BatteryStatus> {
		const batteryStatusDto = await getBatteryStatus(vehicleId);

		return mapBatteryStatusDtoToBatteryStatus(batteryStatusDto);
	},

	async wakeUpVehicle(vehicleId: string): Promise<null> {
		await wakeUp(vehicleId);

		return null;
	},
	async getLockStatus(vehicleId: string): Promise<LockStatusReturn> {
		const lockStatusDto = await getLockStatus(vehicleId);

		return mapVehicleLockStatusDtoToLockStatus(lockStatusDto);
	},
	async updateLockStatus(
		vehicleId: string,
		lockStatus: LockStatusDto
	): Promise<LockStatusReturn> {
		const lockStatusDto = await changeLock(vehicleId, { status: lockStatus });

		return mapVehicleLockStatusDtoToLockStatus(lockStatusDto);
	},
	async updateImmobilizerStatus(
		vehicleId: string,
		immobilizerStatus: LockStatusDto
	): Promise<LockStatusReturn> {
		const lockStatusDto = await changeImmobilizer(vehicleId, {
			status: immobilizerStatus,
		});

		return mapVehicleLockStatusDtoToLockStatus(lockStatusDto);
	},
	async getSupportLink(vehicleId: string): Promise<string> {
		// Call the API endpoint to get the support page link
		const supportLink = await getSupportPageLink(vehicleId);

		return supportLink;
	},
	async refreshBatteryStatus(vehicleId: string): Promise<BatteryStatus> {
		const batteryStatusDto = await refreshBattery(vehicleId);

		return mapBatteryStatusDtoToBatteryStatus(batteryStatusDto);
	},

	async getVehicleLocation(vehicleId: string): Promise<Location> {
		const locationDto = await getLocation(vehicleId);

		return toLocation(locationDto);
	},

	async updateVehicleLocation(vehicleId: string): Promise<Location> {
		const locationDto = await updateLocation(vehicleId);

		return toLocation(locationDto);
	},
	async updateVehicleDeviceInfo(
		vehicleId: string,
		deviceInfo: TelematicsUpdateRequestDto
	) {
		await updateDevice(vehicleId, {
			providerId: deviceInfo.providerId,
			providerDeviceId: deviceInfo.providerDeviceId,
			phoneNumber: deviceInfo.phoneNumber,
			maxRange: deviceInfo.maxRange,
			bydMobilizeHack: deviceInfo.bydMobilizeHack,
			keyfobCheck: deviceInfo.keyfobCheck,
			ignitionCheck: deviceInfo.ignitionCheck,
			alwaysUseBluetooth: deviceInfo.alwaysUseBluetooth,
		});
	},

	async getVehicleDeviceInfo(vehicleId: string): Promise<TelematicsDeviceInfo> {
		const device = await getDeviceByVehicle(vehicleId);

		return device;
	},

	async createVehicleDevice(device: TelematicsCreateRequestDto) {
		await createDevice({
			vehicleId: device.vehicleId,
			providerId: device.providerId,
			providerDeviceId: device.providerDeviceId,
			phoneNumber: device.phoneNumber,
			maxRange: device.maxRange,
			bydMobilizeHack: device.bydMobilizeHack,
			keyfobCheck: device.keyfobCheck,
			ignitionCheck: device.ignitionCheck,
			alwaysUseBluetooth: device.alwaysUseBluetooth,
		});
	},

	async deleteVehicleDevice(vehicleId: string) {
		await deleteDevice(vehicleId);
	},

	async fetchProviders(): Promise<Array<ProviderId> | undefined> {
		const providersDtoResponse = await getProviders();

		return providersDtoResponse.providers;
	},

	async getDeviceByDeviceId(deviceId: string): Promise<TelematicsDeviceInfo> {
		const device = await getDeviceByDeviceId(deviceId);

		return device;
	},

	async getMyVehicles(): Promise<Array<Vehicle>> {
		const response = await getMyVehicles();

		return response.vehicles.map((dto) => toVehicle(dto));
	},
	async getVehicleClientPreferences(
		vehicleId: string
	): Promise<VehicleClientPreference> {
		const response = await getVehicleClientPreference(vehicleId);

		return response;
	},
	async updateVehicleClientPreferences(
		vehicleId: string,
		updateVehicleClientPreferenceRequest: UpdateVehicleClientPreferenceRequest
	): Promise<VehicleClientPreference> {
		const updateVehicleClientPreferenceRequestDto: UpdateVehicleClientPreferenceRequestDto =
			{
				switchMode: updateVehicleClientPreferenceRequest.switchMode,
				personalClientId: toUpdatableField(
					updateVehicleClientPreferenceRequest.personalClientId
				),
				currentClientId: toUpdatableField(
					updateVehicleClientPreferenceRequest.currentClientId
				),
			};

		const response = await updateVehicleClientPreference(
			vehicleId,
			updateVehicleClientPreferenceRequestDto
		);

		return response;
	},

	async updateVehicle(
		vehicleId: string,
		data: UpdateVehicleRequestDto
	): Promise<Vehicle> {
		const response = await updateVehicle(vehicleId, data);
		return toVehicleDetail(response);
	},

	/**
	 * Puts the vehicle out of fleet, for good: stamps Out of fleet since, sets
	 * Phased out and cancels the bookings ending after now. BattAdmin only.
	 */
	async putVehicleOutOfFleet(vehicleId: string): Promise<Vehicle> {
		return toVehicleDetail(await putVehicleOutOfFleet(vehicleId));
	},

	/**
	 * The ids of the bookings that block an Operational status change: awaiting
	 * approval, approved or running, and ending after now. The same search the
	 * backend runs before it refuses the change (the fleet search maps
	 * `startPeriod` onto the engine's `period`, whose `start` filters on
	 * "ends after"), so an overdue running booking
	 * doesn't count. Ids only: the drawer links to them and shows nothing else.
	 */
	async getStatusChangeBlockingBookingIds(
		vehicleId: string
	): Promise<Array<string>> {
		const response = await searchBookings({
			vehicleIds: [vehicleId],
			statuses: OPEN_BOOKING_STATUSES,
			startPeriod: { start: new Date().toISOString() },
		});

		return (response.bookings ?? []).flatMap((booking) =>
			booking.bookingReference?.id ? [booking.bookingReference.id] : []
		);
	},

	/**
	 * The bookings an out-of-fleet date at `from` touches: still open
	 * (awaiting approval, approved or running) and ending after it — the
	 * sweep's own selection, through the same "ends after" reading of
	 * `startPeriod.start` as above. Finished bookings aren't asked for: the
	 * sweep has nothing to do with them.
	 */
	async getBookingsEndingAfter(
		vehicleId: string,
		from: string
	): Promise<Array<OutOfFleetBooking>> {
		const response = await searchBookings({
			vehicleIds: [vehicleId],
			statuses: OPEN_BOOKING_STATUSES,
			startPeriod: { start: from },
		});

		return (response.bookings ?? []).flatMap(
			({ bookingReference, activatedDate }) =>
				bookingReference?.id
					? [
							{
								id: bookingReference.id,
								status: bookingReference.status,
								activatedDate: cleanWebApiDateString(activatedDate),
								plannedEnd: cleanWebApiDateString(
									bookingReference.plannedPeriod.end
								),
							},
						]
					: []
		);
	},

	/**
	 * Every open booking of the vehicle: awaiting approval, approved or
	 * running, with no period. Unlike the "ends after" searches above, this
	 * finds an overdue booking too — one still running past its planned end.
	 * Open bookings don't pile up (approved ones expire), so the list stays
	 * short.
	 */
	async getOpenBookings(
		vehicleId: string
	): Promise<Array<PutOutOfFleetBooking>> {
		const response = await searchBookings({
			vehicleIds: [vehicleId],
			statuses: OPEN_BOOKING_STATUSES,
		});

		return (response.bookings ?? []).flatMap(
			({ bookingReference, activatedDate }) =>
				bookingReference?.id
					? [
							{
								id: bookingReference.id,
								status: bookingReference.status,
								driverName:
									bookingReference.user.displayName ||
									bookingReference.user.userName,
								activatedDate: cleanWebApiDateString(activatedDate),
								plannedStart: cleanWebApiDateString(
									bookingReference.plannedPeriod.start
								),
								plannedEnd: cleanWebApiDateString(
									bookingReference.plannedPeriod.end
								),
							},
						]
					: []
		);
	},

	async getDamagesForVehicle(vehicleId: string): Promise<Array<Damage>> {
		const response = await getDamages(vehicleId);
		return response.items.map(toDamage);
	},

	async updateDamageStatus(
		vehicleId: string,
		damageId: string,
		status: DamageStatusDto
	): Promise<void> {
		await updateDamageStatus(vehicleId, damageId, { status });
	},

	async getVehicleEventsPaginated(
		vehicleId: string,
		page: number,
		size: number
	) {
		const response = await searchBookingEvents({ vehicleId }, { page, size });
		return {
			items: response.items,
			totalElements: response.totalElements,
		};
	},

	async getVehicleDamage3DData(
		vehicleId: string
	): Promise<FleetDamageModelDataDto> {
		// TODO: Replace with real API call when backend PR merges and API is regenerated.
		// Target: GET /fleet/incident/v1/vehicles/{vehicleId}/damages/3d-model/data
		//  → returns FleetDamageModelDataDto
		// Source: https://github.com/Battmobility/be.battmobility.bff/pull/586
		return Promise.resolve(getMockDamage3DModelData(vehicleId));

		// Uncomment when API is available:
		// return customInstance<FleetDamageModelDataDto>({
		// 	url: `/fleet/incident/v1/vehicles/${vehicleId}/damages/3d-model/data`,
		// 	method: "GET",
		// });
	},

	async createVehicleDamage3D(
		vehicleId: string,
		body: FleetCreateDamageRequestDto
	): Promise<void> {
		// TODO: Replace with generated endpoint when backend PR #586 merges.
		// Target: POST /fleet/incident/v1/vehicles/{vehicleId}/damages
		//  ← body: FleetCreateDamageRequestDto
		mockCreateDamage(vehicleId, body);
		return Promise.resolve();

		// Uncomment when API is available:
		// await customInstance<void>({
		// 	url: `/fleet/incident/v1/vehicles/${vehicleId}/damages`,
		// 	method: "POST",
		// 	headers: { "Content-Type": "application/json" },
		// 	data: body,
		// });
	},

	async deleteVehicleDamage3D(
		vehicleId: string,
		damageId: string
	): Promise<void> {
		// TODO: Replace with generated endpoint when backend PR #586 merges.
		// Target: DELETE /fleet/incident/v1/vehicles/{vehicleId}/damages/{damageId}
		mockDeleteDamage(vehicleId, damageId);
		return Promise.resolve();

		// Uncomment when API is available:
		// await customInstance<void>({
		// 	url: `/fleet/incident/v1/vehicles/${vehicleId}/damages/${damageId}`,
		// 	method: "DELETE",
		// });
	},

	async updateVehicleDamage3D(
		vehicleId: string,
		damageId: string,
		body: FleetCreateDamageRequestDto
	): Promise<void> {
		// TODO: Replace with generated endpoint when backend PR #586 merges.
		// Target: PUT /fleet/incident/v1/vehicles/{vehicleId}/damages/{damageId}
		//  ← body: FleetCreateDamageRequestDto (same shape as create)
		mockUpdateDamage(vehicleId, damageId, body);
		return Promise.resolve();

		// Uncomment when API is available:
		// await customInstance<void>({
		// 	url: `/fleet/incident/v1/vehicles/${vehicleId}/damages/${damageId}`,
		// 	method: "PUT",
		// 	headers: { "Content-Type": "application/json" },
		// 	data: body,
		// });
	},

	async toggleVehicleDamage3DStatus(
		vehicleId: string,
		damageId: string,
		status: FleetDamageStatusDto
	): Promise<void> {
		// TODO: Replace with generated endpoint when backend PR #586 merges.
		// Target: PUT /fleet/incident/v1/vehicles/{vehicleId}/damages/{damageId}/status
		//  ← body: FleetUpdateDamageStatusRequestDto
		mockToggleDamageStatus(vehicleId, damageId, status);
		return Promise.resolve();

		// Uncomment when API is available:
		// await customInstance<void>({
		// 	url: `/fleet/incident/v1/vehicles/${vehicleId}/damages/${damageId}/status`,
		// 	method: "PUT",
		// 	headers: { "Content-Type": "application/json" },
		// 	data: { status },
		// });
	},
};
